import { useEffect } from "react";
import {
  isExternalUrl,
  openExternalLink
} from "../utils/nativeUtils/openExternalLink";

/**
 * Whether the current context is a React Native WebView.
 */
const isNativeWebView = (): boolean =>
  typeof window !== "undefined" && "ReactNativeWebView" in window;

/**
 * Finds the closest ancestor `<a>` element from a given event target.
 * Returns `null` if the click did not originate within an anchor.
 */
const findAnchorElement = (
  target: EventTarget | null
): HTMLAnchorElement | null => {
  if (!(target instanceof HTMLElement)) return null;
  return target.closest("a");
};

/**
 * Global interceptor that prevents external URLs from loading inside
 * the native webview where there is no navigation mechanism to return.
 *
 * It uses two complementary web-side strategies, backed by native-side
 * handlers (`onOpenWindow` and `onShouldStartLoadWithRequest` in App.tsx)
 * that catch navigation requests from cross-origin iframes where these
 * web-side strategies cannot reach:
 *
 * 1. **Document-level click listener** (capture phase) — catches clicks
 *    on `<a>` tags in the same document (e.g. navigation menu links,
 *    payment links). In a native webview, links with `target="_blank"`
 *    are also intercepted because the webview has no concept of tabs.
 *
 * 2. **`window.open` override** — catches programmatic attempts to open
 *    external URLs from any context, including cross-origin iframes that
 *    call `window.parent.open()` or `window.top.open()`.
 *
 * NOTE: Intercom iframe sandboxing was intentionally removed. Intercom's
 * messenger frame (`intercom-messenger-frame`) has no `src` attribute —
 * it is populated programmatically. Applying a `sandbox` attribute after
 * the browsing context has already been created does not retroactively
 * enforce restrictions in WKWebView. Instead it puts the iframe into an
 * inconsistent state where popup requests (`target="_blank"`) are
 * silently dropped — neither `onOpenWindow` nor
 * `onShouldStartLoadWithRequest` fires. Without the sandbox attribute,
 * WKWebView correctly routes popup requests through
 * `createWebViewWithConfiguration:forNavigationAction:`, which fires our
 * `onOpenWindow` handler and opens the link in Safari.
 */
export const useExternalLinkInterceptor = (): void => {
  useEffect(() => {
    const isNative = isNativeWebView();

    // ── 1. Document-level click listener ──────────────────────────────
    const handleClick = (event: MouseEvent): void => {
      const anchor = findAnchorElement(event.target);
      if (!anchor) return;

      const href = anchor.getAttribute("href");
      if (!href || !isExternalUrl(href)) return;

      // On desktop/web, links with target="_blank" open in a new tab
      // natively — no interception needed. In React Native WebView
      // there are no tabs so we must always intercept external links.
      const target = anchor.getAttribute("target");
      if (!isNative && target === "_blank") return;

      event.preventDefault();
      event.stopPropagation();
      openExternalLink(href);
    };

    document.addEventListener("click", handleClick, true);

    // ── 2. window.open override ───────────────────────────────────────
    const originalWindowOpen = window.open.bind(window);

    window.open = (
      url?: string | URL,
      target?: string,
      features?: string
    ): WindowProxy | null => {
      const urlString = url?.toString() ?? "";

      if (urlString && isExternalUrl(urlString) && isNative) {
        openExternalLink(urlString);
        return null;
      }

      return originalWindowOpen(url, target, features);
    };

    // ── Cleanup ───────────────────────────────────────────────────────
    return () => {
      document.removeEventListener("click", handleClick, true);
      window.open = originalWindowOpen;
    };
  }, []);
};
